import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade6EngineeringLesson.css";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

/** Helper to shuffle arrays */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade6Engineering3DDemo:
 *  Cycles among 3 advanced 3D models:
 *   - Dam
 *   - Rocket
 *   - High-Rise
 */
function Grade6Engineering3DDemo() {
  const items = [
    {
      name: "Dam",
      description: "Dams store water and produce hydroelectric power!",
      fact: "Engineers ensure dams withstand water pressure and earthquakes.",
    },
    {
      name: "Rocket",
      description: "Rockets launch satellites, astronauts, and more into space!",
      fact: "Engineers calculate thrust and fuel for stable flight.",
    },
    {
      name: "High-Rise",
      description: "Skyscrapers reach great heights, using strong frames and designs.",
      fact: "Engineers test wind load, materials, and foundation strength!",
    },
  ];

  const [index, setIndex] = useState(0);

  const nextItem = () => setIndex((prev) => (prev + 1) % items.length);

  return (
    <div className="g6eng-3d-demo-container">
      <h4>3D Engineering Demo: {items[index].name}</h4>
      <p className="g6eng-demo-description">{items[index].description}</p>
      <p className="g6eng-demo-fact">
        <strong>Fun Fact:</strong> {items[index].fact}
      </p>
      <button className="g6eng-3d-next-btn" onClick={nextItem}>
        See Another Example
      </button>

      <div style={{ width: "300px", height: "200px", margin: "10px auto", background: "#f0f0f0" }}>
        <Canvas camera={{ position: [0, 2, 5], fov: 50 }}>
          <OrbitControls enablePan={false} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} />
          {/* Basic ground plane */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]}>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color="#ddd" />
          </mesh>

          {/* Render shape by name */}
          <Grade6EngineeringShape name={items[index].name} />
        </Canvas>
      </div>
    </div>
  );
}

/** 
 * Grade6EngineeringShape:
 *  - Dam: a wide curved wall
 *  - Rocket: a cylinder+cone shape 
 *  - High-Rise: tall boxes
 */
function Grade6EngineeringShape({ name }) {
  if (name === "Dam") {
    return (
      <group>
        {/* Dam as a thick, curved wall */}
        <mesh rotation={[0, Math.PI / 2, 0]} position={[0, 0, 0]}>
          {/* We'll mimic a dam with a torus slice or a simple big curved box. 
              For simplicity, let's do a "torus" section with large radius. */}
          <torusGeometry args={[1.5, 0.4, 8, 16, Math.PI / 2]} />
          <meshStandardMaterial color="#9e9e9e" />
        </mesh>
        {/* Water plane behind the dam (just a plane) */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, -0.5]}>
          <planeGeometry args={[2, 2]} />
          <meshStandardMaterial color="#64b5f6" opacity={0.7} transparent />
        </mesh>
      </group>
    );
  } else if (name === "Rocket") {
    return (
      <group>
        {/* Main rocket body => cylinder */}
        <mesh>
          <cylinderGeometry args={[0.2, 0.2, 2, 16]} />
          <meshStandardMaterial color="#cfd8dc" />
        </mesh>
        {/* Nose cone => smaller cone on top */}
        <mesh position={[0, 1, 0]}>
          <coneGeometry args={[0.2, 0.4, 16]} />
          <meshStandardMaterial color="#b0bec5" />
        </mesh>
        {/* Fins => 3 or 4 small boxes or triangular prisms at base */}
        <mesh position={[0.15, -0.5, 0]} rotation={[0, 0, 0]}>
          <boxGeometry args={[0.05, 0.3, 0.2]} />
          <meshStandardMaterial color="#8d6e63" />
        </mesh>
        <mesh position={[-0.15, -0.5, 0]} rotation={[0, 0, 0]}>
          <boxGeometry args={[0.05, 0.3, 0.2]} />
          <meshStandardMaterial color="#8d6e63" />
        </mesh>
      </group>
    );
  } else if (name === "High-Rise") {
    return (
      <group>
        {/* Base tower => big rectangular block */}
        <mesh position={[0, 0, 0]}>
          <boxGeometry args={[1, 2.5, 1]} />
          <meshStandardMaterial color="#7986cb" />
        </mesh>
        {/* Another smaller block on top => to look layered */}
        <mesh position={[0, 1.75, 0]}>
          <boxGeometry args={[0.8, 1, 0.8]} />
          <meshStandardMaterial color="#5c6bc0" />
        </mesh>
        {/* small antenna */}
        <mesh position={[0, 2.75, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 0.3, 8]} />
          <meshStandardMaterial color="#37474f" />
        </mesh>
      </group>
    );
  }
  return null;
}

/** 
 * Additional advanced engineering challenge, e.g. 
 * "Mega Project Challenge" 
 */
function MegaProjectChallenge() {
  const challenges = [
    {
      name: "Design a Mega Bridge",
      description: "Span a wide river while supporting heavy traffic!",
      example: "Consider cable-stayed or suspension designs for a stable crossing.",
    },
    {
      name: "Space Habitat",
      description: "How might we build living modules on the Moon or Mars?",
      example: "Use local materials, radiation shielding, and greenhouse modules!",
    },
    {
      name: "Underwater Tunnel",
      description: "Connecting two land areas below water safely!",
      example: "Engineers handle water pressure and sealing with strong materials.",
    },
  ];

  const [index, setIndex] = useState(0);
  const nextChallenge = () => setIndex((prev) => (prev + 1) % challenges.length);

  return (
    <div className="g6eng-challenge-container">
      <h4>Mega Project Challenge: {challenges[index].name}</h4>
      <p className="g6eng-challenge-description">{challenges[index].description}</p>
      <p className="g6eng-challenge-example">
        <strong>Tip:</strong> {challenges[index].example}
      </p>
      <button className="g6eng-next-challenge-btn" onClick={nextChallenge}>
        See Another Challenge
      </button>
    </div>
  );
}

/** Main Grade6EngineeringLesson */
export default function Grade6EngineeringLesson() {
  const [selectedTopic, setSelectedTopic] = useState(null);

  /** Lesson content with 2 main topics */
  const lessonContent = {
    renewableEnergy: {
      title: "Renewable Energy Systems",
      description: "Learn how engineers harness wind, water, and solar power!",
      example: "Example: Hydroelectric dams or solar farms for clean energy!",
      steps: [
        "Engineers measure resource availability (wind speed, sunlight hours, water flow).",
        "They design turbines, panels, or generators to convert energy efficiently.",
        "Grid integration requires careful planning to handle variable energy output.",
        "Reducing fossil fuels helps protect the environment!",
        <Grade6Engineering3DDemo key="g6eng-demo" />,
      ],
    },
    structuralAnalysis: {
      title: "Structural Analysis",
      description: "Engineers ensure buildings, bridges, and towers stay safe!",
      example: "Example: A skyscraper that withstands strong winds or earthquakes.",
      steps: [
        "Calculations check forces, stresses, and strains in materials.",
        "Simulations help predict how a structure behaves under load.",
        "Engineers consider safety factors and building codes.",
        "They use advanced math & software to design stable structures!",
        <MegaProjectChallenge key="g6eng-challenge" />,
      ],
    },
  };

  /** Quiz Pools => 25 each => 50 total */
  // 1) renewableEnergyPool
  const renewableEnergyPool = [
    {
      question: "What is a key benefit of renewable energy?",
      correctAnswer: "It’s cleaner, reducing pollution",
      options: ["It never runs out of fuel", "It’s cleaner, reducing pollution", "It costs nothing at all"],
    },
    {
      question: "Solar panels convert?",
      correctAnswer: "Sunlight into electricity",
      options: ["Wind into heat", "Sunlight into electricity", "Water into hydrogen"],
    },
    // ... (add up to 25)
    {
      question: "Which factor do engineers check for wind turbines?",
      correctAnswer: "Local wind speed data",
      options: [
        "Random guess on location",
        "Local wind speed data",
        "No factor needed, any place works",
      ],
    },
  ];

  // 2) structuralAnalysisPool
  const structuralAnalysisPool = [
    {
      question: "Structural analysis ensures a building can?",
      correctAnswer: "Withstand expected loads and forces",
      options: [
        "Float on water easily",
        "Never need maintenance",
        "Withstand expected loads and forces",
      ],
    },
    {
      question: "Why do engineers add safety factors?",
      correctAnswer: "To account for uncertainties and extra loads",
      options: [
        "They love using bigger numbers",
        "To account for uncertainties and extra loads",
        "Buildings must be super cheap",
      ],
    },
    // ... (add up to 25)
    {
      question: "What do simulations help with in structural analysis?",
      correctAnswer: "Predict how a structure behaves under stress or load",
      options: [
        "Just create colorful pictures",
        "Predict how a structure behaves under stress or load",
        "Eliminate the need for math",
      ],
    },
  ];

  /** States for quick/final quiz */
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 min

  /** Timer effect */
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      // time up
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  /** Generate Quick Quiz question */
  const generateQuiz = (topic) => {
    let questionPool = topic === "renewableEnergy" ? renewableEnergyPool : structuralAnalysisPool;
    const item = questionPool[Math.floor(Math.random() * questionPool.length)];
    setQuiz({
      question: item.question,
      correctAnswer: item.correctAnswer,
      options: shuffleArray([...item.options]),
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  /** Quick quiz handle answer */
  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    // auto-next question after short delay (optional)
    setTimeout(() => {
      if (selectedTopic) generateQuiz(selectedTopic);
    }, 1500);
  };

  /** Start final quiz => combine 50 questions */
  const startFinalQuiz = () => {
    const combined = [...renewableEnergyPool, ...structuralAnalysisPool];
    const finalSet = shuffleArray(combined);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  /** Final quiz handle */
  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex]?.correctAnswer === choice) {
      setFinalQuizScore((prev) => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex((prev) => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g6eng-page">
      <h1 className="g6eng-title">Grade 6 Engineering Lessons</h1>
      <p className="g6eng-description">
        Dive deeper into advanced topics like renewable energy and structural analysis!
      </p>

      <div className="g6eng-topics">
        <button
          className="g6eng-topic-card"
          onClick={() => {
            setSelectedTopic("renewableEnergy");
            generateQuiz("renewableEnergy");
          }}
        >
          Renewable Energy
        </button>
        <button
          className="g6eng-topic-card"
          onClick={() => {
            setSelectedTopic("structuralAnalysis");
            generateQuiz("structuralAnalysis");
          }}
        >
          Structural Analysis
        </button>
      </div>

      {selectedTopic && (
        <div className="g6eng-lesson-quiz-container">
          {/* Lesson Content (Left) */}
          <div className="g6eng-lesson-content">
            <h2 className="g6eng-lesson-title">{lessonContent[selectedTopic].title}</h2>
            <p className="g6eng-lesson-description">
              {lessonContent[selectedTopic].description}
            </p>
            <p className="g6eng-lesson-example">
              <strong>{lessonContent[selectedTopic].example}</strong>
            </p>
            <ul className="g6eng-lesson-steps">
              {lessonContent[selectedTopic].steps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ul>
          </div>

          {/* Right: Quick + Final Quizzes */}
          <div className="g6eng-quiz-double-container">
            {/* Quick Quiz */}
            <div className="g6eng-quick-quiz-container">
              <h2 className="g6eng-quiz-title">Quick Quiz!</h2>
              {quiz && (
                <>
                  <p className="g6eng-quiz-question">{quiz.question}</p>
                  <div className="g6eng-quiz-options">
                    {quiz.options.map((opt, i) => (
                      <button
                        key={i}
                        className={`g6eng-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                        onClick={() => handleAnswerClick(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {feedback && <p className="g6eng-quiz-feedback">{feedback}</p>}
                </>
              )}
            </div>

            {/* Final Quiz */}
            <div className="g6eng-final-quiz-container">
              <h2 className="g6eng-quiz-title">Final Quiz</h2>
              {!finalQuizActive && (
                <button className="g6eng-final-quiz-button" onClick={startFinalQuiz}>
                  Start Final Quiz (50 Questions)
                </button>
              )}
              {finalQuizActive && (
                <div className="g6eng-final-quiz-content">
                  <p>
                    Time Left: {Math.floor(timeLeft / 60)}:
                    {String(timeLeft % 60).padStart(2, "0")}
                  </p>
                  <p>
                    Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
                  </p>
                  <p className="g6eng-quiz-question">
                    {finalQuizQuestions[finalQuizIndex].question}
                  </p>
                  <div className="g6eng-quiz-options">
                    {finalQuizQuestions[finalQuizIndex].options.map((choice, i) => (
                      <button key={i} onClick={() => handleFinalQuizAnswer(choice)}>
                        {choice}
                      </button>
                    ))}
                  </div>
                  <p>Score: {finalQuizScore}</p>
                  {finalQuizIndex === finalQuizQuestions.length - 1 && (
                    <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Link to="/engineering" className="g6eng-back-button">
        &larr; Back to Engineering Page
      </Link>
    </div>
  );
}
